import axios from 'axios'
import { API_CONFIG } from '@/config/api'
import {
  clearAuth,
  tryGetAccessToken,
  saveAccessToken,
  isAccessTokenExpired,
} from '@/utils/authUtils'

const axiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL
})

let isRefreshing = false
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let failedRequests: any[] = []

// Add a request interceptor
axiosInstance.interceptors.request.use(
  async (config) => {
    if (config.url?.startsWith('/refresh') || config.url?.startsWith('/login')) {
      // Skip refresh and login requests
      return config
    }

    const accessToken = tryGetAccessToken()

    if (accessToken) {
      if (isAccessTokenExpired()) {
        // Token is expired, try to refresh
        const newToken = await refreshAccessToken()
        if (newToken) {
          config.headers.Authorization = `Bearer ${newToken}`
        }
      } else {
        config.headers.Authorization = `Bearer ${accessToken}`
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Add a response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedRequests.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return axiosInstance(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const token = await refreshAccessToken()
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`
          // Process failed requests
          failedRequests.forEach((request) => {
            request.resolve(token)
          })
          return axiosInstance(originalRequest)
        }
      } catch (error) {
        failedRequests.forEach((request) => {
          request.reject(error)
        })
        window.location.href = '/login'
        return Promise.reject(error)
      } finally {
        isRefreshing = false
        failedRequests = []
      }
    }

    return Promise.reject(error)
  },
)

// Define a type for objects that might contain Date values
type DateObject = {
  [key: string]: string | number | boolean | Date | DateObject | DateObject[]
}

/**
 * Utility function to recursively convert ISO date strings to Date objects
 * @param obj
 * @returns
 */
function convertDates<T extends DateObject>(obj: T): T {
  // check for null or undefined
  if (!obj) {
    return obj
  }

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key]
      if (
        typeof value === 'string' &&
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(value)
      ) {
        ;(obj[key] as unknown) = new Date(value) // Convert ISO string to Date object
      } else if (typeof value === 'object' && value !== null) {
        if (Array.isArray(value)) {
          ;(obj[key] as unknown) = value.map((item) =>
            typeof item === 'object' && item !== null ? convertDates(item as DateObject) : item,
          )
        } else {
          convertDates(value as DateObject) // Recursively process nested objects
        }
      }
    }
  }
  return obj
}

// Axios response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    if (response.data && typeof response.data === 'object') {
      response.data = convertDates(response.data as DateObject) // Convert date strings in the response
    }
    return response
  },
  (error) => {
    return Promise.reject(error)
  },
)

async function refreshAccessToken() {
  try {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) throw new Error('No refresh token')

    const response = await axios.post(`${API_CONFIG.baseURL}/refresh`, {
      refreshToken: refreshToken,
    })

    const { accessToken, refreshToken: newRefreshToken } = response.data

    console.debug('Token refreshed:', accessToken)
    saveAccessToken(accessToken, newRefreshToken, response.data.expiresIn)

    return accessToken
  } catch (error) {
    console.error('Error refreshing token:', error)
    clearAuth()
    return null
  }
}

export default axiosInstance
